// @mui material components
import { createTheme } from "@mui/material/styles";

// Colour tokens generated from the base colour
import { buildTokens, DEFAULT_BASE_COLOR } from "./tokens/palette";

// Material Dashboard 2 React base styles
import { makeColors } from "./base/colors";
import breakpoints from "./base/breakpoints";
import makeTypography from "./base/typography";
import makeBoxShadows from "./base/boxShadows";
import makeBorders from "./base/borders";
import makeGlobals from "./base/globals";

// Material Dashboard 2 React helper functions
import boxShadow from "./functions/boxShadow";
import hexToRgb from "./functions/hexToRgb";
import linearGradient from "./functions/linearGradient";
import pxToRem from "./functions/pxToRem";
import rgba from "./functions/rgba";

// Material Dashboard 2 React components base styles for @mui material components
import sidenav from "./components/sidenav";
import list from "./components/list";
import listItem from "./components/list/listItem";
import listItemText from "./components/list/listItemText";
import card from "./components/card";
import cardMedia from "./components/card/cardMedia";
import cardContent from "./components/card/cardContent";
import button from "./components/button";
import iconButton from "./components/iconButton";
import input from "./components/form/input";
import inputLabel from "./components/form/inputLabel";
import inputOutlined from "./components/form/inputOutlined";
import textField from "./components/form/textField";
import menu from "./components/menu";
import menuItem from "./components/menu/menuItem";
import switchButton from "./components/form/switchButton";
import divider from "./components/divider";
import tableContainer from "./components/table/tableContainer";
import tableHead from "./components/table/tableHead";
import tableCell from "./components/table/tableCell";
import linearProgress from "./components/linearProgress";
import breadcrumbs from "./components/breadcrumbs";
import slider from "./components/slider";
import avatar from "./components/avatar";
import tooltip from "./components/tooltip";
import appBar from "./components/appBar";
import tabs from "./components/tabs";
import tab from "./components/tabs/tab";
import stepper from "./components/stepper";
import step from "./components/stepper/step";
import stepConnector from "./components/stepper/stepConnector";
import stepLabel from "./components/stepper/stepLabel";
import stepIcon from "./components/stepper/stepIcon";
import select from "./components/form/select";
import formControlLabel from "./components/form/formControlLabel";
import formLabel from "./components/form/formLabel";
import checkbox from "./components/form/checkbox";
import radio from "./components/form/radio";
import autocomplete from "./components/form/autocomplete";
import container from "./components/container";
import popover from "./components/popover";
import buttonBase from "./components/buttonBase";
import icon from "./components/icon";
import svgIcon from "./components/svgIcon";
import link from "./components/link";
import dialog from "./components/dialog";
import dialogTitle from "./components/dialog/dialogTitle";
import dialogContent from "./components/dialog/dialogContent";
import dialogContentText from "./components/dialog/dialogContentText";
import dialogActions from "./components/dialog/dialogActions";
import dataGrid from "./components/dataGrid";

/**
 * The app theme for a mode ("light" | "dark") and a base colour, which drives every colour.
 * @since NEXT_VERSION
 */
export function createAppTheme({ mode = "light", baseColor = DEFAULT_BASE_COLOR } = {}) {
  const generated = buildTokens(baseColor);
  const tokens = generated[mode];
  const colors = makeColors(generated, mode);
  const typography = makeTypography({ colors });
  const boxShadows = makeBoxShadows({ colors });
  const borders = makeBorders({ colors });
  const context = { colors, typography, boxShadows, borders, tokens, mode };
  // Overrides that depend on colours are factories; the rest are plain objects.
  const resolve = (override) => (typeof override === "function" ? override(context) : override);

  return createTheme({
    breakpoints: { ...breakpoints },
    palette: { ...colors, mode },
    typography: { ...typography },
    boxShadows: { ...boxShadows },
    borders: { ...borders },
    tokens,
    baseColor: { picked: generated.base, accent: generated.accent, adjusted: generated.adjusted },
    functions: {
      boxShadow,
      hexToRgb,
      linearGradient,
      pxToRem,
      rgba,
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ...makeGlobals(context),
          ...container,
        },
      },
      // Surfaces use the token colours; MUI's dark-mode elevation tint would grey them.
      MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
      MuiDrawer: resolve(sidenav),
      MuiList: resolve(list),
      MuiListItem: resolve(listItem),
      MuiListItemText: resolve(listItemText),
      MuiCard: resolve(card),
      MuiCardMedia: resolve(cardMedia),
      MuiCardContent: resolve(cardContent),
      MuiDataGrid: resolve(dataGrid),
      MuiButton: resolve(button),
      MuiIconButton: resolve(iconButton),
      MuiInput: resolve(input),
      MuiInputLabel: resolve(inputLabel),
      MuiOutlinedInput: resolve(inputOutlined),
      MuiTextField: resolve(textField),
      MuiMenu: resolve(menu),
      MuiMenuItem: resolve(menuItem),
      MuiSwitch: resolve(switchButton),
      MuiDivider: resolve(divider),
      MuiTableContainer: resolve(tableContainer),
      MuiTableHead: resolve(tableHead),
      MuiTableCell: resolve(tableCell),
      MuiLinearProgress: resolve(linearProgress),
      MuiBreadcrumbs: resolve(breadcrumbs),
      MuiSlider: resolve(slider),
      MuiAvatar: resolve(avatar),
      MuiTooltip: resolve(tooltip),
      MuiAppBar: resolve(appBar),
      MuiTabs: resolve(tabs),
      MuiTab: resolve(tab),
      MuiStepper: resolve(stepper),
      MuiStep: resolve(step),
      MuiStepConnector: resolve(stepConnector),
      MuiStepLabel: resolve(stepLabel),
      MuiStepIcon: resolve(stepIcon),
      MuiSelect: resolve(select),
      MuiFormControlLabel: resolve(formControlLabel),
      MuiFormLabel: resolve(formLabel),
      MuiCheckbox: resolve(checkbox),
      MuiRadio: resolve(radio),
      MuiAutocomplete: resolve(autocomplete),
      MuiPopover: resolve(popover),
      MuiButtonBase: resolve(buttonBase),
      MuiIcon: resolve(icon),
      MuiSvgIcon: resolve(svgIcon),
      MuiLink: resolve(link),
      MuiDialog: resolve(dialog),
      MuiDialogTitle: resolve(dialogTitle),
      MuiDialogContent: resolve(dialogContent),
      MuiDialogContentText: resolve(dialogContentText),
      MuiDialogActions: resolve(dialogActions),
    },
  });
}

export default createAppTheme();
